import { useState, useMemo } from "react";
import { useConfirm } from "@omit/react-confirm-dialog";
import type { Model } from "@bryntum/schedulerpro";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  Check,
  X,
  ActivitySquare,
  Sparkles,
  Loader2,
} from "lucide-react";
import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useContextForm } from "../../context/ContextFormProvider";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import type { BoardContextStatement } from "@/bryntum-lib/models/BoardContextStatement";
import { ActiveContextReadOnlyBanner } from "./ActiveContextReadOnlyBanner";
import { useContextPage } from "../../context/ContextPageContext";

export interface ContextViewOptions {
  onDuplicateContext?: () => void;
  isDuplicatingContext?: boolean;
}

const MARKDOWN_STRIP_RE = /[#*_`~>-]/g;

/** Shared preview styling — soft surface + orange accent (light cream / dark neutral). */
const previewStatementSurface = cn(
  "rounded-lg border border-border/60 bg-orange-50/70 dark:bg-card",
  "border-l-[3px] border-l-dashed border-l-orange-500",
  "shadow-sm"
);

const AiSuggestedBadge = () => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md px-2 py-0.5 shrink-0",
      "text-[10px] font-bold uppercase tracking-wide",
      "bg-orange-500/10 text-orange-700 dark:text-orange-400",
      "ring-1 ring-orange-500/15"
    )}
  >
    <Sparkles className="h-2.5 w-2.5" aria-hidden />
    AI Suggested
  </span>
);

// ── Inline statement editor ───────────────────────────────────────────────────
interface StatementCardProps {
  statement: BoardContextStatement;
  group?: BoardContextGroup;
  groupStatementCount?: number;
  isReadOnly?: boolean;
  onSaved?: () => void;
}

const StatementCard = ({ statement, group, groupStatementCount = 0, isReadOnly = false, onSaved }: StatementCardProps) => {
  const { project } = useSwivuBryntumContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [headline, setHeadline] = useState(statement.headline ?? "");
  const [body, setBody] = useState(statement.body);
  const confirm = useConfirm();
  const isPreview = Boolean(statement.isPreview);

  const handleEdit = () => {
    setHeadline(statement.headline ?? "");
    setBody(statement.body);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setHeadline(statement.headline ?? "");
    setBody(statement.body);
  };

  const acceptPreviewStatement = (fields?: { headline: string | null; body: string }) => {
    statement.set({
      ...(fields ?? {}),
      isPreview: false,
    });
    if (group?.isPreview) {
      group.set({ isPreview: false });
    }
  };

  const handleSave = async () => {
    if (!project || !body.trim()) return;
    setIsSaving(true);
    try {
      statement.set({
        headline: headline.trim() || null,
        body: body.trim(),
      });
      await project.sync();
      setIsEditing(false);
      onSaved?.();
    } catch {
      showToast({ msg: "Failed to save statement.", type: "error", duration: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAccept = async () => {
    if (!project) return;
    setIsAccepting(true);
    try {
      acceptPreviewStatement();
      await project.sync();
    } catch {
      showToast({ msg: "Failed to accept suggestion.", type: "error", duration: 3000 });
    } finally {
      setIsAccepting(false);
    }
  };

  const isLastStatementInPreviewGroup =
    Boolean(group?.isPreview) && groupStatementCount === 1;

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Delete statement",
      description: isLastStatementInPreviewGroup
        ? "This AI suggestion and its group will be removed."
        : isPreview
          ? "This AI suggestion will be removed."
          : "This statement will be permanently removed from the context.",
      customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-3">
          <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={proceed}>Delete</Button>
        </div>
      ),
    });
    if (!confirmed) return;

    if (!project) return;
    setIsDeleting(true);
    try {
      const stmtStore = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
      stmtStore?.remove(statement as unknown as Model);
      if (isLastStatementInPreviewGroup && group) {
        const grpStore = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
        grpStore?.remove(group as unknown as Model);
      }
      await project.sync();
    } catch {
      showToast({ msg: "Failed to delete statement.", type: "error", duration: 3000 });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isEditing) {
    return (
      <div
        className={cn(
          "rounded-lg border p-4 space-y-3",
          isPreview
            ? cn(previewStatementSurface, "p-4")
            : "border-primary/30 bg-primary/[0.02]"
        )}
      >
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Headline <span className="normal-case font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Short headline…"
            disabled={isSaving}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSaving}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !body.trim()}
            className="gap-1.5 h-7 text-xs"
          >
            <Check className="h-3 w-3" />
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving}
            className="gap-1.5 h-7 text-xs text-muted-foreground"
          >
            <X className="h-3 w-3" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (isPreview) {
    return (
      <div className={cn(previewStatementSurface, "px-4 py-3.5 space-y-2")}>
        <div className="flex flex-wrap items-center gap-2 gap-y-1">
          <AiSuggestedBadge />
          {statement.headline && (
            <p className="text-sm font-medium text-foreground leading-snug">{statement.headline}</p>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{statement.body}</p>
        {!isReadOnly && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <Button
              size="sm"
              onClick={handleAccept}
              disabled={isAccepting}
              className="h-7 gap-1.5 text-xs bg-orange-600 hover:bg-orange-700 text-white shadow-none"
            >
              {isAccepting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              Accept
            </Button>
            <Button size="sm" variant="ghost" onClick={handleEdit} className="h-7 gap-1.5 text-xs">
              <Pencil className="h-3 w-3" />
              Edit
            </Button>
            <Button
              size="sm" variant="ghost" onClick={handleDelete} disabled={isDeleting}
              className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
              Dismiss
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="group relative py-3.5 pr-1 first:pt-2 border-t border-border/30 first:border-t-0">
      {statement.headline && (
        <p className="text-[10px] font-bold text-primary/80 uppercase tracking-widest mb-1.5 leading-tight">
          {statement.headline}
        </p>
      )}
      <p className="text-[13px] text-foreground/80 leading-relaxed pr-14">{statement.body}</p>

      {!isReadOnly && (
        <div className="absolute top-3 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-100">
          <button
            type="button"
            onClick={handleEdit}
            title="Edit"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            title="Delete"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-destructive/15 hover:text-destructive transition-colors disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

// ── Group section ─────────────────────────────────────────────────────────────
interface GroupSectionProps {
  group: BoardContextGroup;
  statements: BoardContextStatement[];
  isReadOnly?: boolean;
}

const GroupSection = ({ group, statements, isReadOnly = false }: GroupSectionProps) => {
  const { project } = useSwivuBryntumContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(group.title);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isAcceptingGroup, setIsAcceptingGroup] = useState(false);
  const confirm = useConfirm();
  const isPreviewGroup = Boolean(group.isPreview);

  const groupStatements = statements.filter((s) => s.group === group.id);

  const handleSaveTitle = async () => {
    if (!project || !titleValue.trim()) return;
    setIsSavingTitle(true);
    try {
      group.set({ title: titleValue.trim() });
      await project.sync();
      setIsEditingTitle(false);
    } catch {
      showToast({ msg: "Failed to save group title.", type: "error", duration: 3000 });
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleAcceptGroup = async () => {
    if (!project) return;
    setIsAcceptingGroup(true);
    try {
      group.set({ isPreview: false });
      for (const s of groupStatements) {
        if (s.isPreview) s.set({ isPreview: false });
      }
      await project.sync();
    } catch {
      showToast({ msg: "Failed to accept group.", type: "error", duration: 3000 });
    } finally {
      setIsAcceptingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    const confirmed = await confirm({
      title: "Delete group",
      description: `"${group.title}" and all its statements will be removed.`,
      customActions: ({ confirm: proceed, cancel }: { confirm: () => void; cancel: () => void }) => (
        <div className="space-x-3">
          <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          <Button variant="destructive" size="sm" onClick={proceed}>Delete group</Button>
        </div>
      ),
    });
    if (!confirmed || !project) return;

    try {
      const stmtStore = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
      stmtStore?.remove(groupStatements as unknown as Model[]);
      const grpStore = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
      grpStore?.remove(group as unknown as Model);
      await project.sync();
    } catch {
      showToast({ msg: "Failed to delete group.", type: "error", duration: 3000 });
    }
  };

  return (
    <div className={cn(
      "rounded-xl overflow-hidden border",
      isPreviewGroup
        ? "bg-orange-500/[0.07] border-orange-500/20"
        : "bg-muted/40 border-border/25"
    )}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => !isEditingTitle && setIsExpanded(!isExpanded)}
        onKeyDown={(e) => e.key === "Enter" && !isEditingTitle && setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 px-4 py-3 group/title cursor-pointer select-none"
      >
        <span className="text-muted-foreground shrink-0">
          {isExpanded
            ? <ChevronDown className="h-3.5 w-3.5" />
            : <ChevronRight className="h-3.5 w-3.5" />
          }
        </span>

        {isEditingTitle && !isReadOnly ? (
          <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTitle();
                if (e.key === "Escape") { setIsEditingTitle(false); setTitleValue(group.title); }
              }}
              disabled={isSavingTitle}
              className="flex-1 rounded border border-primary/40 bg-background px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <button type="button" onClick={handleSaveTitle} disabled={isSavingTitle} className="text-primary"><Check className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => { setIsEditingTitle(false); setTitleValue(group.title); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isPreviewGroup && <Sparkles className="h-3.5 w-3.5 text-orange-400 shrink-0" aria-hidden />}
            <h3 className="text-[13px] font-semibold text-foreground flex-1 truncate tracking-tight">{group.title}</h3>

            {!isReadOnly && (
              <div
                className="flex items-center gap-0.5 opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                {isPreviewGroup && (
                  <button type="button" onClick={handleAcceptGroup} disabled={isAcceptingGroup} title="Accept all"
                    className="h-5 px-1.5 flex items-center gap-1 rounded text-[10px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                    {isAcceptingGroup ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    Accept all
                  </button>
                )}
                <button type="button" onClick={() => setIsEditingTitle(true)} title="Rename"
                  className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <Pencil className="h-3 w-3" />
                </button>
                <button type="button" onClick={handleDeleteGroup} title="Delete group"
                  className="h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-0">
          {groupStatements.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 italic">No statements yet.</p>
          ) : (
            groupStatements.map((s) => (
              <StatementCard key={s.id} statement={s} group={group} groupStatementCount={groupStatements.length} isReadOnly={isReadOnly} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Context health band ───────────────────────────────────────────────────────
const ContextHealthBand = ({ markdown }: { markdown: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const stripped = markdown.replace(MARKDOWN_STRIP_RE, "").replace(/\s+/g, " ").trim();
  const preview = stripped.slice(0, 260);

  return (
    <div className="rounded-xl overflow-hidden mb-5 bg-primary/[0.06]">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <ActivitySquare className="h-3.5 w-3.5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-primary">Summary</span>
          {!isExpanded && preview && (
            <p className="text-xs text-foreground/50 mt-0.5 line-clamp-1 leading-relaxed">
              {preview}{preview.length < stripped.length ? "…" : ""}
            </p>
          )}
        </div>
        <ChevronDown className={cn("h-3.5 w-3.5 text-primary/50 transition-transform duration-200 shrink-0", isExpanded && "rotate-180")} />
      </button>
      {isExpanded && (
        <div className="px-4 pb-4">
          <MarkdownRenderer content={markdown} className="text-foreground/75 text-sm" />
        </div>
      )}
    </div>
  );
};

// ── Main context view ─────────────────────────────────────────────────────────
export const ContextView = ({
  onDuplicateContext,
  isDuplicatingContext = false,
}: ContextViewOptions = {}) => {
  const {
    groups,
    statements,
    boardContextRecord,
    hasOpenAIKey,
  } = useContextForm();
  const { canModify } = useContextPage();

  const isActiveLocked = boardContextRecord?.isActive === true;
  const isReadOnly = isActiveLocked || !hasOpenAIKey || !canModify;

  const sortedGroups = useMemo(
    () =>
      [...groups].sort((a, b) => {
        if (a.isPreview !== b.isPreview) return a.isPreview ? 1 : -1;
        return a.order - b.order;
      }),
    [groups]
  );

  const isAwaitingGroups =
    sortedGroups.length === 0 && boardContextRecord?.status === "completed";

  if (isAwaitingGroups) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {(isActiveLocked || !hasOpenAIKey) && (
        <ActiveContextReadOnlyBanner
          onDuplicate={isActiveLocked ? onDuplicateContext : undefined}
          isDuplicating={isDuplicatingContext}
          canDuplicate={canModify}
        />
      )}

      <div className="flex-1 overflow-y-auto px-6 py-5">
        {boardContextRecord?.contextHealth && (
          <ContextHealthBand markdown={boardContextRecord.contextHealth} />
        )}

        {sortedGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-muted-foreground">No context groups yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedGroups.map((group) => (
              <GroupSection key={group.id} group={group} statements={statements} isReadOnly={isReadOnly} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
